import { graphql, useStaticQuery } from 'gatsby';

import type { Popup } from '@insify/exit-popup';
import type { Country } from '~models/country';
import type { ContentfulComponentLink } from '~components/ComponentLink';
import type {
  ContentfulComponentLinkGroup,
  ContentfulComponentLinkOrContentfulComponentLinkGroup,
  ContentfulComponentLinkOrContentfulComponentLinkMenu,
} from '~components/types';

export type ContentfulSiteConfig = {
  headerPinnedItems: ContentfulComponentLinkOrContentfulComponentLinkMenu[] | null;
  headerDynamicItems: ContentfulComponentLinkOrContentfulComponentLinkMenu[] | null;
  headerMyInsify: ContentfulComponentLink | null;
  headerCtaItems: ContentfulComponentLinkOrContentfulComponentLinkGroup | null;
  footerItems: ContentfulComponentLinkGroup[];
  exitPopups: Popup[];
};

type SiteConfig = Record<Country, ContentfulSiteConfig>;

const query = graphql`
  query SiteConfigQuery {
    nl: contentfulSiteConfig(contentfulid: { eq: "nl" }) {
      ...SiteConfig
    }
    de: contentfulSiteConfig(contentfulid: { eq: "de" }) {
      ...SiteConfig
    }
    fr: contentfulSiteConfig(contentfulid: { eq: "fr" }) {
      ...SiteConfig
    }
  }

  fragment SiteConfig on ContentfulSiteConfig {
    headerPinnedItems {
      ...ComponentLinkOrComponentLinkMenu
    }
    headerDynamicItems {
      ...ComponentLinkOrComponentLinkMenu
    }
    headerMyInsify {
      ...ComponentLink
    }
    headerCtaItems {
      ...ComponentLinkOrComponentLinkGroup
    }
    footerItems {
      ...ComponentLinkGroup
    }
    exitPopups {
      __typename
      name
      paths
      pathMatchStrategy
      showAfter
      slides {
        title
        description
        image {
          url
          mimeType
        }
      }
      animationInterval
      primaryButtonLabel
      primaryButtonTarget
      secondaryButtonLabel
      secondaryButtonTarget
    }
  }

  fragment ComponentLinkGroup on ContentfulComponentLinkGroup {
    __typename
    label
    items {
      ... on ContentfulComponentLink {
        ...ComponentLink
      }
    }
  }

  fragment ComponentLinkOrComponentLinkGroup on ContentfulComponentLinkContentfulComponentLinkGroupUnion {
    ... on ContentfulComponentLink {
      ...ComponentLink
    }
    ... on ContentfulComponentLinkGroup {
      ...ComponentLinkGroup
    }
  }

  fragment ComponentLinkOrComponentLinkMenu on ContentfulComponentLinkContentfulComponentLinkMenuUnion {
    ... on ContentfulComponentLink {
      ...ComponentLink
    }
    ... on ContentfulComponentLinkMenu {
      ...ComponentLinkMenu
    }
  }

  fragment ComponentLinkMenu on ContentfulComponentLinkMenu {
    __typename
    label
    column1 {
      ...ComponentLinkOrComponentLinkGroup
    }
    column2 {
      ...ComponentLinkOrComponentLinkGroup
    }
    extraContent {
      ... on ContentfulComponentCard {
        ...ComponentCard
      }
      ... on ContentfulComponentTrustpilot {
        ...ComponentTrustpilot
      }
    }
    link {
      ...ComponentLink
    }
  }

  fragment ComponentCard on ContentfulComponentCard {
    __typename
    image {
      ...MixedMedia
    }
    title
    description
    link {
      ...ComponentLink
    }
    backgroundColor
  }
`;

export const useSiteConfig = (country: Country) => useStaticQuery<SiteConfig>(query)[country];
